import { useState, useCallback } from "react";
import toast from "react-hot-toast";

export const useGPS = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCurrentPosition = useCallback((options = {}) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser"));
        return;
      }

      // Require secure context for reliable geolocation on most browsers
      if (typeof window !== "undefined" && !window.isSecureContext) {
        reject(
          new Error(
            "Location requires HTTPS. Please open the link over HTTPS and enable location."
          )
        );
        return;
      }

      const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      };

      const finalOptions = { ...defaultOptions, ...options };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          const timestamp = new Date().toISOString();

          resolve({
            lat: latitude,
            lng: longitude,
            accuracy: accuracy,
            ts: timestamp,
          });
        },
        (error) => {
          let errorMessage = "Failed to get location";

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage =
                "Location access denied. Please allow location access and try again.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage =
                "Location information is unavailable. Please try again.";
              break;
            case error.TIMEOUT:
              errorMessage =
                "Location request timed out. Move to open sky or try again.";
              break;
            default:
              // Some browsers surface generic errors; provide actionable guidance
              errorMessage =
                "Unable to get location. Ensure GPS is ON, browser has permission, and you are on HTTPS.";
              break;
          }

          reject(new Error(errorMessage));
        },
        finalOptions
      );
    });
  }, []);

  const captureGPS = useCallback(
    async (options = {}) => {
      setIsLoading(true);
      setError(null);

      try {
        const gps = await getCurrentPosition(options);

        // Validate GPS accuracy
        if (gps.accuracy > 100) {
          throw new Error(
            `GPS accuracy is ${Math.round(gps.accuracy)}m. Please try in a better location.`
          );
        }

        return gps;
      } catch (err) {
        setError(err.message);
        toast.error(err.message, { duration: 5000 });
        // Callers check this so the message isn't shown twice
        err.toastShown = true;
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [getCurrentPosition]
  );

  const requestPermission = useCallback(async () => {
    try {
      if (!navigator.permissions) {
        throw new Error("Permissions API not supported");
      }

      const permission = await navigator.permissions.query({
        name: "geolocation",
      });

      if (permission.state === "denied") {
        throw new Error("Location permission denied");
      }

      return permission.state;
    } catch (err) {
      console.warn("Permission check failed:", err.message);
      return "unknown";
    }
  }, []);

  return {
    captureGPS,
    getCurrentPosition,
    requestPermission,
    isLoading,
    error,
  };
};
